import { validateLabelsAndName } from './label-util';
import { METRIC_CLS, METRIC_LCP, OTHER_PATH, WHITELISTED_METRICS_PATHS } from './metrics';

import type PrometheusBatcher from 'js/src/libs/prometheusBatcher';

function normalizePath(pathname: string): string {
    const basePaths = WHITELISTED_METRICS_PATHS.filter((p) => p !== OTHER_PATH);

    if (pathname === '') {
        return '/';
    }

    for (const allowedPath of basePaths) {
        if (pathname === allowedPath || pathname.startsWith(allowedPath)) {
            return allowedPath;
        }
    }

    return OTHER_PATH;
}

/**
 * Core Web Vitals tracker class that observes LCP and CLS metrics
 * and sends them to Prometheus via the aggregation gateway
 */
class CoreWebVitals {
    static instance: CoreWebVitals;

    prometheus: typeof PrometheusBatcher | null;
    lcpValue: number;
    clsScore: number;
    currentPath: string;
    lcpObserver: PerformanceObserver | null;
    clsObserver: PerformanceObserver | null;

    static getInstance() {
        if (CoreWebVitals.instance) {
            return CoreWebVitals.instance;
        }
        CoreWebVitals.instance = new CoreWebVitals();
        return CoreWebVitals.instance;
    }

    constructor() {
        this.lcpValue = 0;
        this.clsScore = 0;
        this.currentPath = normalizePath(window.location.pathname);
        this.lcpObserver = null;
        this.clsObserver = null;

        // Get global PrometheusBatcher instance
        if (window.Prometheus) {
            this.prometheus = window.Prometheus;
        } else {
            this.prometheus = null;
        }

        if (this.prometheus) {
            this.startObserving();
            this.setupUnloadHandlers();
        }
    }

    startObserving() {
        // Monitor Largest Contentful Paint (LCP)
        this.lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1];
            this.lcpValue = lastEntry.startTime;
        });
        this.lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // Monitor Layout Shifts (CLS)
        this.clsObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach((entry) => {
                const layoutShift = entry as PerformanceEntry & {
                    hadRecentInput?: boolean;
                    value: number;
                };
                if (!layoutShift.hadRecentInput) {
                    this.clsScore += layoutShift.value;
                }
            });
        });
        this.clsObserver.observe({ type: 'layout-shift', buffered: true });
    }

    setupUnloadHandlers() {
        // Send metrics when user leaves the page
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.sendMetrics();
            }
        });

        // Also send on page unload as a fallback
        window.addEventListener('pagehide', () => {
            this.sendMetrics();
        });
    }

    sendMetrics() {
        if (!this.prometheus) {
            return;
        }

        if (this.lcpValue > 0) {
            const lcpSeconds = this.lcpValue / 1000.0;
            const labels = METRIC_LCP.getLabels(this.currentPath);
            if (validateLabelsAndName(METRIC_LCP.name, labels)) {
                this.prometheus.observe(METRIC_LCP, lcpSeconds, labels);
            }
        }

        if (this.clsScore > 0) {
            const labels = METRIC_CLS.getLabels(this.currentPath);
            if (validateLabelsAndName(METRIC_CLS.name, labels)) {
                this.prometheus.observe(METRIC_CLS, this.clsScore, labels);
            }
        }
    }

    disconnect() {
        if (this.lcpObserver) {
            this.lcpObserver.disconnect();
        }
        if (this.clsObserver) {
            this.clsObserver.disconnect();
        }
    }
}

export function getGlobal(): Window & typeof globalThis {
    if (typeof window !== 'undefined') {
        return window;
    } else if (typeof global !== 'undefined') {
        return global as Window & typeof globalThis;
    } else if (typeof self !== 'undefined') {
        return self;
    } else {
        throw new Error('unable to find global context');
    }
}

let coreWebVitalsInstance: CoreWebVitals;

if (getGlobal().CoreWebVitals) {
    coreWebVitalsInstance = getGlobal().CoreWebVitals;
} else {
    coreWebVitalsInstance = CoreWebVitals.getInstance();
    getGlobal().CoreWebVitals = coreWebVitalsInstance;
}

export default coreWebVitalsInstance;
